import * as Yup from "yup";
import { useFormik } from "formik";
import React from "react";

import { Checkbox, FormControlLabel, FormGroup, FormHelperText, TextField } from "@mui/material";

import { QuestionaireBasePage } from "./QuestionaireBasePage";
import { OptionDefinitionDto, Question, QuestionDefinitionDto } from "./questionaire-types";

type MultipleOptions = {
  [key: string]: string | boolean;
};

const getTextFieldName = (option: OptionDefinitionDto) => `${option.key}_Text`;

export const createInitialValues = (options: OptionDefinitionDto[]) => {
  const initialValues: MultipleOptions = {};
  options.forEach((o) => {
    initialValues[o.key] = false;
    if (o.isTextInput) {
      initialValues[getTextFieldName(o)] = "";
    }
  });
  return initialValues;
};

type MultipleOptionsPageProps = {
  questionDefinition: QuestionDefinitionDto;
  onNext: (question: Question) => void;
  isSaving: boolean;
  isFrenchLanguage:boolean
};

export const MultipleOptionsPage = (props: MultipleOptionsPageProps) => {
  const { questionDefinition, onNext, isSaving } = props;

  const countDefinedProps = (value: MultipleOptions) => {
    let count = 0;
    for (const key in value) {
      if (value[key]) {
        count++;
      }
    }

    return count;
  };

  let validationObject = {};
  questionDefinition.options?.forEach((o) => {
    if (o.isTextInput) {
      validationObject = {
        ...validationObject,
        [o.key]: Yup.boolean(),
        [getTextFieldName(o)]: Yup.string().when(o.key, {
          is: true,
          then: Yup.string().required("Please input the text field"),
        }),
      };
    }
  });

  const formik = useFormik({
    initialValues: createInitialValues(questionDefinition.options!),
    validationSchema: Yup.object()
      .shape(validationObject)
      .test({
        name: "requiredOption",
        test: function (value: MultipleOptions) {
          if (countDefinedProps(value) > 0) {
            return true;
          }

          return this.createError({
            path: "allOptions",
            message: !props.isFrenchLanguage?"Please select an option":"Veuillez sélectionner une option",
          });
        },
      })
      .test({
        name: "maxSelectedOptions",
        test: function (value: MultipleOptions) {
          if (!questionDefinition.maxNumberOfAnswers) {
            return true;
          }

          if (countDefinedProps(value) > questionDefinition.maxNumberOfAnswers) {
            return this.createError({
              path: "allOptions",
              message: `Please only select maximum of ${questionDefinition.maxNumberOfAnswers} options`,
            });
          }

          return true;
        },
      }),
    onSubmit: (value: MultipleOptions) => {
      const answers = Object.keys(value)
        .filter((k) => value[k] === true)
        .map((k) => {
          let label;
          const option = questionDefinition.options!.find((o) => o.key === k)!;
          if (option && option.isTextInput) {
            label = value[getTextFieldName(option)] as string;
          } else {
            label = option.label;
          }

          return {
            key: k,
            label,
          };
        });
      const question = {
        key: questionDefinition.key,
        label: questionDefinition.title,
        answers,
      };
      onNext(question);
    },
  });

  return (
    <QuestionaireBasePage questionDefinition={questionDefinition} formik={formik} isSaving={isSaving} isFrenchLanguage={props.isFrenchLanguage}>
      <FormGroup>
        {questionDefinition.options?.map((o) => {
          const textFieldName = getTextFieldName(o);
          return (
            <div key={o.key}>
              <FormControlLabel label={o.label} name={o.key} onChange={formik.handleChange} control={<Checkbox value={formik.values[o.key]} />} />
              {o.isTextInput && formik.values[o.key] && (
                <TextField
                  name={textFieldName}
                  onChange={formik.handleChange}
                  value={formik.values[textFieldName]}
                  error={formik.touched[textFieldName] && Boolean(formik.errors[textFieldName])}
                  helperText={formik.touched[textFieldName] ? formik.errors[textFieldName] : ""}
                  size="small"
                  fullWidth
                  sx={{ paddingLeft: 4 }}
                />
              )}
            </div>
          );
        })}
      </FormGroup>
      <FormHelperText error={Boolean(formik.errors.allOptions)}>{formik.errors.allOptions}</FormHelperText>
    </QuestionaireBasePage>
  );
};
